# Talent Search Connect

This use case provides technical implementation details for the Talent Search Connect product.
You can find more information on its [product page].

[SEEK Talent Search] enables hirers to proactively search for and connect with suitable candidates.
It’s powered by over 44 million SEEK Profiles across the Asia Pacific.

Talent Search Connect integrates [SEEK Talent Search] with candidate management systems to address some of the efficiency bottlenecks hirers experience with segregated proactive sourcing and candidate management workflows.

The SEEK API supports a number of features to enable a more seamless experience for hirers:

- [Widgets](/use-cases/proactive-sourcing/widgets)

  Widgets prompt hirers to consider proactive sourcing activities in the context of their candidate management workflow.

- [Sharing candidates](/use-cases/proactive-sourcing/sharing-a-candidate)

  Candidate details and their past [interaction history] with the hirer can be uploaded and kept in sync,
  enabling hirers to determine at a glance if they have an existing relationship with the candidate.
  Hirers are able to view their previous interactions with the candidate when viewing a profile in Talent Search,
  reducing time spent cross-referencing candidate information with your software.

- [Unposted positions](/use-cases/proactive-sourcing/unposted-positions)

  Unposted positions allow your software to upload positions your hirers are actively recruiting for.
  This position information is presented to the hirer when they are purchasing SEEK candidate profiles to allow them to select which position opening the purchase should be associated with.

- [Exporting purchased profiles](/use-cases/proactive-sourcing/profile-purchases)

  When a hirer purchases a candidate’s profile within Talent Search,
  it is made available for export directly into a position in your software,
  reducing the amount of manual handling of candidate information required by the hirer.

All SEEK API operations for the Talent Search Connect use case require use of a [partner token](/auth/partner-tokens).

[interaction history]: /use-cases/proactive-sourcing/interaction-history
[seek talent search]: https://au.employer.seek.com/products/talentsearch
[product page]: https://au.employer.seek.com/partners/connect-with-seek#talent-search-connect

## Interaction diagrams

### Sharing candidates

```mermaid Sharing candidate information
graph LR
    s1-->tsc1
    s2-->tsc1
    s3-->tsc1
    p1-->s1
    p2-->s2
    p3-->s3
    subgraph Talent Search
      tsc1(Update partner candidate index)
    end
    subgraph SEEK API
      s1(uploadCandidate)
      s2(updateUploadedCandidate)
      s3(createCandidateProcessHistoryItem)
    end
    subgraph Partner
      p1(Create candidate)
      p2(Update candidate)
      p3(Create candidate interaction)
    end
```

### Sharing unposted positions

```mermaid Sharing open positions
graph LR
    s1-->tsc1
    s2-->tsc1
    p1-->s1
    p2-->s2
    subgraph Talent Search
      tsc1(Update partner position index)
    end
    subgraph SEEK API
      s1(createUnpostedPositionProfileForOpening)
      s2(deleteUnpostedPositionProfileForOpening)
    end
    subgraph Partner
      p1(Create position)
      p2(Complete hiring for position)
    end
```

### Exporting purchased profiles

```mermaid Export purchased profiles
graph TB
    s1-->p1
    p2-->s2
    p1-->p2
    s2-->p3
    p3-->p4
    p4-- No -->p5
    p4-- Yes -->p6
    p5-->p7
    p6-->p7
    subgraph Partner
      p1(Receive notification)
      p2(Request for SEEK candidate profile and resume)
      p3(Process candidate profile and resume)
      p4{Candidate exists?}
      p5(Create candidate)
      p6(Update candidate)
      p7(Add candidate to position)
    end
    subgraph SEEK API
      s1(CandidateProfilePurchased event)
      s2(candidateProfile)
    end
```
